import * as React from "react";
import { useHistory } from "react-router-dom";
import { useSeo } from "../../hooks";

const TermsOfServicePage: React.FunctionComponent = () => {
    const history = useHistory();

    useSeo({
        title: "Oberin Terms of Service and Privacy Policy",
        description:
            "Read the official Oberin terms of service, privacy policy, forum rules, and macro policy.",
        canonical: "https://www.oberin.be/termsOfService",
        robots: "index,follow",
    });

    /***************************************************
     * Event handlers
     ***************************************************/
    const handleGameTermsOfServiceClicked = React.useCallback(
        (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
            event.preventDefault();
            history.replace("/termsOfService#game-terms-of-service");
            document.querySelector("#game-terms-of-service")?.scrollIntoView({
                behavior: "smooth",
            });
        },
        [history],
    );

    const handleGamePrivacyStatementClicked = React.useCallback(
        (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
            event.preventDefault();
            history.replace("/termsOfService#game-privacy-statement");
            document.querySelector("#game-privacy-statement")?.scrollIntoView({
                behavior: "smooth",
            });
        },
        [history],
    );

    const handleForumAndDiscordRulesClicked = React.useCallback(
        (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
            event.preventDefault();
            history.replace("/termsOfService#forum-discord-rules");
            document.querySelector("#forum-discord-rules")?.scrollIntoView({
                behavior: "smooth",
            });
        },
        [history],
    );

    const handleForumPrivacyStatementClicked = React.useCallback(
        (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
            event.preventDefault();
            history.replace("/termsOfService#forum-privacy-statement");
            document.querySelector("#forum-privacy-statement")?.scrollIntoView({
                behavior: "smooth",
            });
        },
        [history],
    );

    const handleUnattendedMacroPolicyClicked = React.useCallback(
        (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
            event.preventDefault();
            history.replace("/termsOfService#unattended-macro-policy");
            document.querySelector("#unattended-macro-policy")?.scrollIntoView({
                behavior: "smooth",
            });
        },
        [history],
    );

    /***************************************************
     * Render
     ***************************************************/
    return (
        <div className="w-100 px-2">
            {/* Content overview */}
            <div className="box-shadow">
                <div className="title-bar px-2">
                    <h1 className="mb-0 title-bar-heading">Terms of Service and Privacy Policy</h1>
                </div>
                <div className="bg-light-gray p-2 bl-xs br-xs bb-xs d-flex flex-column">
                    <ul
                        style={{
                            margin: 0,
                            paddingInlineStart: 20,
                        }}
                    >
                        <li>
                            <a
                                href="#game-terms-of-service"
                                className="sidebar-navigation-list-item-link"
                                onClick={handleGameTermsOfServiceClicked}
                            >
                                Game: Terms of Service
                            </a>
                        </li>
                        <li>
                            <a
                                href="#game-privacy-statement"
                                className="sidebar-navigation-list-item-link"
                                onClick={handleGamePrivacyStatementClicked}
                            >
                                Game: Privacy Statement
                            </a>
                        </li>
                        <li>
                            <a
                                href="#forum-discord-rules"
                                className="sidebar-navigation-list-item-link"
                                onClick={handleForumAndDiscordRulesClicked}
                            >
                                Forum and Discord Rules
                            </a>
                        </li>
                        <li>
                            <a
                                href="#forum-privacy-statement"
                                className="sidebar-navigation-list-item-link"
                                onClick={handleForumPrivacyStatementClicked}
                            >
                                Forum: Privacy Statement
                            </a>
                        </li>
                        <li>
                            <a
                                href="#unattended-macro-policy"
                                className="sidebar-navigation-list-item-link"
                                onClick={handleUnattendedMacroPolicyClicked}
                            >
                                Unattended macro policy
                            </a>
                        </li>
                    </ul>
                </div>
            </div>

            {/* Game: Terms of service */}
            <div id="game-terms-of-service" className="mt-2 box-shadow">
                <div className="title-bar px-2">
                    <span>Game: Terms of Service</span>
                </div>
                <div className="bg-light-gray p-2 bl-xs br-xs bb-xs d-flex flex-column">
                    <span>
                        While you are a member of the Oberin service, you must
                        abide by the following rules. If you break{" "}
                        <strong>any</strong> of these rules, your Oberin
                        account, and your right to use the Oberin service, may
                        be terminated immediatly. These rules are:
                    </span>
                    <ol
                        style={{
                            margin: 0,
                            paddingInlineStart: 20,
                        }}
                    >
                        <li>
                            You may not harass, threaten, embarrass or cause
                            distress, unwanted attention or discomfort to
                            another player. This includes unwanted following,
                            chasing, stalking, etc. You must leave a player
                            alone if they ask you to do so.
                        </li>
                        <li>
                            You may not use any offensive or sexually explicit
                            language.
                        </li>
                        <li>
                            You may not use any unlawful, harmful, threatening,
                            abusive, harassing, defamatory, vulgar, obscene,
                            hateful, racially, ethnically, religiously, or
                            otherwise objectionable language toward any other
                            player. And any other words or actions which offend,
                            inflame, upset or humiliate.
                        </li>
                        <li>
                            You may not organize any guilds or groups that are
                            based on, or espouse, any racist, sexist,
                            anti-religious, anti-ethnic, anti-gay, or other
                            hate-mongering philosophy.
                        </li>
                        <li>
                            You may not obstruct another player's movement such
                            as by standing in a doorway, or blocking their
                            escape from danger.
                        </li>
                        <li>
                            You may not falsify your email address which is
                            required at the time you create a character.
                        </li>
                        <li>
                            You may not exploit any bug in the Oberin product or
                            service to gain unfair advantage in the game and you
                            will not communicate the existence of any such bug
                            (either directly or through public posting) to any
                            other user. You must inform the admininstration of
                            any such bug you discover.
                        </li>
                        <li>
                            You may not allow another person to use your
                            account.
                        </li>
                        <li>
                            You may not have more than one character logged on
                            at a time.
                        </li>
                        <li>
                            You may not "macro" your characters while not
                            attending the game. This is called unattended
                            macroing. Unattended macroing means being away from
                            your Oberin session or away from your computer while
                            your character repeats a series of actions for any
                            length of time. If your character is performing a
                            skill or action you must respond to a GM when
                            confronted, if you are found to be "macroing" the
                            penalty will be severe.
                        </li>
                        <li>
                            You may not cheat in any way listed or not listed
                            here. You may not use any articifial means to
                            advance your character. You may not attempt to
                            bypass the way the game is intended to be played.
                        </li>
                        <li>
                            You may not use loopholes in the TOS. You may not do
                            things that, though not specifically listed here,
                            obviously conflict with natural gameplay of Oberin.
                        </li>
                        <li>
                            You may not do anything else that interferes with
                            the ability of other Oberin users to enjoy playing
                            the game, or that increases the expense or
                            difficulty of maintaining the Oberin service for the
                            enjoyment of all its users.
                        </li>
                        <li>
                            "Hacking" or "cracking" the client or modifying the
                            Oberin Client or Oberin Server in any way, to bypass
                            normal game functions, is not allowed. Those found
                            doing so will be banned immediatly.
                        </li>
                        <li>
                            If for any reason you are in disagreement or
                            conflict between players or GM's (Game Masters),
                            Oberin GM's have the final word in all matters.
                        </li>
                    </ol>
                </div>
            </div>

            {/* Game: Privacy Statement */}
            <div id="game-privacy-statement" className="mt-2 box-shadow">
                <div className="title-bar px-2">
                    <span>Game: Privacy Statement</span>
                </div>
                <div className="bg-light-gray p-2 bl-xs br-xs bb-xs d-flex flex-column">
                    The game saves the following data:
                    <ul
                        style={{
                            margin: 0,
                            paddingInlineStart: 20,
                        }}
                    >
                        <li>
                            Mac Address: Verification of which characters are
                            registered on a computer, and where a character was
                            made
                        </li>
                        <li>
                            Email: The email used to make a character. Primarly
                            used for ability to remove a character or recovering
                            a character
                        </li>
                        <li>
                            Username and password (hashed): To log into the game
                        </li>
                        <li>
                            IP Address: Saved to keep track of a connection log
                            for a given player. Used to check for double
                            logging. Can be seen by developers and senior GMs
                            in-game.
                        </li>
                        <li>
                            All data relevant to the characters in-game items,
                            pets, party list and skills.
                        </li>
                    </ul>
                    <br />
                    <strong>
                        None of this data is used for any third-party purposes.
                    </strong>
                </div>
            </div>

            {/* Forum: Privacy Statement */}
            <div id="forum-discord-rules" className="mt-2 box-shadow">
                <div className="title-bar px-2">
                    <span>Forum and Discord Rules</span>
                </div>
                <div className="bg-light-gray p-2 bl-xs br-xs bb-xs d-flex flex-column">
                    <span>
                        <strong>1 - General Conduct</strong>
                    </span>
                    <span>
                        All members of Oberin Discord or Oberin forum agree to
                        abive by the following general guidelines. Depending on
                        the severity of the infractions, penalties can range
                        from anything from warnings to instant bans.
                    </span>
                    <ol
                        type="A"
                        style={{
                            margin: 0,
                            paddingInlineStart: 20,
                        }}
                    >
                        <li>
                            This community operates under a zero-tolerance
                            policy for discriminatory or hate speech of any
                            kind, and all users are expected to refrain from
                            comments which disparage or otherwise target someone
                            on the basis of age, disability, sex, gender
                            identity, sexual orientation, marital status,
                            maternity, race, origin, religion, and any other
                            commonly protected characteristics. Violation of
                            this rule will result in an immediate ban.
                        </li>
                        <li>
                            Any content that would be considered NSFW (“Not Safe
                            for Work”) is also strictly prohibited, including in
                            avatars and usernames.
                        </li>
                        <li>
                            Also strictly prohibited are any forms of bullying,
                            harassment, or any other type of abusive conduct,
                            which includes but is not limited to verbal abuse,
                            insults, epithets, derogatory remarks, and any
                            malicious conduct that a reasonable person would
                            find threatening, intimidating, or humiliating.
                        </li>
                        <li>
                            “Flaming”, or riling up of arguments, is not
                            permitted, even if you didn't start said argument.
                            Regardless of whether you think your points have any
                            merits or not, abusive or inflammatory language will
                            not be tolerated under any circumstances.
                        </li>
                        <li>
                            Certain comments that are not strictly prohibited,
                            but have the potential to escalate into abusive
                            situations, will be considered “Discouraged
                            Commentary” and will also be moderated. This sort of
                            commentary includes any personal arguments,
                            disagreements, or otherwise volatile personal issues
                            that should be limited to private communications.
                            This sort of commentary generally will not incur
                            penalties except in particularly severe instances,
                            but warnings will be issued and any offending
                            messages will be subject to removal.
                        </li>
                        <li>
                            Above all else, remain respectful of other people's
                            opinions and messages, and try to demonstrate and
                            assume good faith.
                        </li>
                        <li>
                            GMs and Moderators may enforce rules at their own
                            discretion - finding loopholes to violate the rules
                            will result in punishment.
                        </li>
                    </ol>

                    <span className="mt-2">
                        <strong>2 - Spamming</strong>
                    </span>
                    <span>
                        Excessive posting in channels, or “spamming”, is not
                        allowed, as this can have a debilitating effect on
                        discussions and tends to discourage other voices from
                        joining in. Generally, and depending on severity, users
                        are warned or temporarily muted when violating this
                        rule. Behavior that will be considered spam includes,
                        but is not limited to:
                    </span>
                    <ol
                        type="A"
                        style={{
                            margin: 0,
                            paddingInlineStart: 20,
                        }}
                    >
                        <li>
                            Making posts that are off topic to any given
                            channel.
                        </li>
                        <li>
                            Posting GIFs in formal and high-volume channels
                            (Discord specifically: #oberin-general,
                            #hunting-party, #rtq-news, #role-playing, and
                            #suggestions-discussion).
                        </li>
                        <li>
                            If, after no one has responded to your initial
                            messages on a topic, you continue to flood the
                            channel with additional comments. Consider this as a
                            loose interpretation of one of the old universal
                            rules of forum etiquette - “Don't Double Post”. In
                            other words, flooding a channel with extra messages,
                            even if they are intended to spawn discourse, tends
                            to have the effect of stifling it.
                        </li>
                        <li>
                            For Voice Chat: screaming, making disturbing
                            background noises, or any other NSFW sounds.
                        </li>
                    </ol>

                    <span className="mt-2">
                        <strong>3 - Penalties</strong>
                    </span>
                    <span>
                        In general, we will follow a “progressive discipline”
                        policy. That is to say, most minor infractions will only
                        incur a warning at first. Repeated or serious
                        infractions can include anything from temporary mutes to
                        permanent mutes, temporary bans, and finally permanent
                        bans.
                    </span>
                    <br />
                    <span>
                        Warnings will be tracked on a rolling basis as a note
                        attached to a user's account. If a user incurs 3
                        warnings within a rolling 7-day period, they will be
                        given a temporary 24-hour mute.
                    </span>
                    <br />
                    <span>
                        Particularly severe violations may result in an
                        immediate ban, especially for violations of Rule 1A.
                    </span>
                    <br />
                    <span>
                        Users that are found to be deliberately attempting to
                        circumvent the Progressive Discipline by "toeing the
                        line", or repeatedly incurring just enough warnings to
                        prevent incurring an actual penalty, will be penalized
                        all the same.
                    </span>

                    <span className="mt-2">
                        <strong>4 - Moderation</strong>
                    </span>
                    <span>
                        Moderators will assist the GM Team in evaluating and
                        enforcing the above rules. Moderators are always
                        required to base their moderation on those rules and
                        policies, and not their own personal opinions.
                    </span>
                    <br />
                    <span>
                        Moderators will be given a fair amount of discretion,
                        but if there is ever any particularly complicated
                        scenarios or reason for doubt, Moderators will consult
                        with GMs before taking action.
                    </span>
                    <br />
                    <span>
                        Moderators will also inform users whenever they remove a
                        users' content or mute them, and should offer helpful
                        rule explanations, tips, and/or links to new or confused
                        users.
                    </span>
                    <br />
                    <span>
                        More information on the Moderator position can be found
                        in the Moderator Application on Discord (see
                        #discord-rules), posted separately.
                    </span>

                    <span className="mt-2">
                        <strong>5 - Contact Us</strong>
                    </span>
                    <span>
                        When you have an equiry, dispute, request, question
                        about an official warning, or something else, you can
                        either contact us through Discord. See the 'Contact us'
                        section on the website, and then specifically on the
                        discord under '#discord-rules'
                    </span>

                    <span className="mt-2">
                        <strong>6 - Mini-modding</strong>
                    </span>
                    <span>
                        "Mini-modding", also known as "backseat moderating", is
                        when a user acts like a moderator or GM without having
                        the authority to do so, and is not allowed. This can
                        include making any kind of nominally helpful
                        correctional comment that would normally fall to a
                        moderator, such as informing people that they are
                        violating our rules or posting off-topic.
                    </span>
                    <br />
                    <span>
                        While your intentions may be pure (i.e., trying to
                        prevent someone from being banned, or protecting the
                        community), it too often results in an escalation of
                        conflict, which runs entirely contrary to the very point
                        of the Rules.
                    </span>
                    <br />
                    <span>
                        If you'd like to give a helpful warning to a fellow user
                        that their post might be breaking rules without alerting
                        a moderator, feel free to send them a Direct Message
                        (Discord), but be sure to keep it professional and
                        polite.
                    </span>
                    <br />
                    <span>
                        If you feel a post requires moderator action, simply
                        report it to the global Moderator account (Discord) -
                        we're always grateful for the support. Just don't post
                        it publicly as it might just make things worse
                    </span>
                </div>
            </div>

            {/* Forum: Privacy Statement */}
            <div id="forum-privacy-statement" className="mt-2 box-shadow">
                <div className="title-bar px-2">
                    <span>Forum: Privacy Statement</span>
                </div>
                <div className="bg-light-gray p-2 bl-xs br-xs bb-xs d-flex flex-column">
                    <span>The forum saves the following data:</span>
                    <ol
                        style={{
                            margin: 0,
                            paddingInlineStart: 20,
                        }}
                    >
                        <li>
                            An account is linked to an email. This email is used
                            to login, for verification and recovery of password.
                            Only admins can see an account's email.
                        </li>
                        <li>
                            Password of an account is safely stored away in a
                            non-readable manner.
                        </li>
                        <li>
                            An account is linked to all the characters and posts
                            made by that account.
                        </li>
                        <li>
                            Local storage and Session storage is used to
                            authenticate a registered user.
                        </li>
                    </ol>
                    <br />
                    <strong>
                        None of this data is used for any third-party purposes.
                    </strong>
                </div>
            </div>

            {/* Unattended macro policy */}
            <div id="unattended-macro-policy" className="mt-2 box-shadow">
                <div className="title-bar px-2">
                    <span>Unattended Macro Policy</span>
                </div>
                <div className="bg-light-gray p-2 bl-xs br-xs bb-xs d-flex flex-column">
                    <span>
                        <strong>
                            Officially posted in Discord by GM Bobley
                        </strong>
                    </span>
                    <br />
                    <span>
                        While the rules have been outlined, and there was a long
                        discussion regarding attended and unattended macroing,
                        after having checked quite a few people in-game, I felt
                        it necessary to make a clear post on our stance
                        concerning <strong>attended</strong> and{" "}
                        <strong>uattended</strong> macroing.
                    </span>
                    <br />
                    <span className="text-uppercase">
                        <strong>attended macroing</strong>
                    </span>
                    <span>
                        This is allowed. Due to the nature of the game, a lot of
                        clicking is required to level up certain skills, or
                        create certain items. This can be detrimental to both
                        someone's health (hands, arms, wrists, ..) and to
                        people's hardware equipment. For this reason, macroing
                        is allowed.
                    </span>
                    <br />
                    <span className="text-uppercase">
                        <strong>unattended macroing</strong>
                    </span>
                    <span>
                        This is simply put, not allowed. You can see this
                        clearly stated in the Terms of Service, which you
                        automatically agree on when you play the game.
                    </span>
                    <br />
                    <span>
                        Extract from the Terms of Service: "unattended macroing
                        means being away from your Oberin session or away from
                        your computer while your character repeats a series of
                        actions for any length of time. If your character is
                        performing a skill or action you must respond to a GM
                        when confronted, if you are found to be "macroing" the
                        penalty will be severe."
                    </span>
                    <br />
                    <span>
                        This means that, at any time, if a GM shows up, on your
                        screen, and you are performing any action in game,
                        whether that's moving around, creating an item,
                        meditating, or simply clicking on your same spot to stay
                        logged, you need to be able to respond to the GM.
                    </span>
                    <br />
                    <span>
                        Inability to respond to the GM, will result in the
                        following <strong>account-wide</strong> (1) penalties:
                    </span>
                    <span>First time: one week in jail</span>
                    <span>Second time: two weeks in jail</span>
                    <span>Third time: one month in jail</span>
                    <span>Fourth time: pit (2)(3)</span>
                    <br />
                    <span>
                        (1): Account-wide means that, if you are found
                        unattended macroing on your cleric, it will count as
                        your first time. If you are then found unattended
                        macroing on your fighter, it will count as your second
                        time. It is not on a per-character basis.
                    </span>
                    <span>
                        (2): Pitting: A pitted character means you lose the
                        entire character. All the items, and all the time you've
                        invested in it, all gone.
                    </span>
                    <span>
                        (3): Anything past the fourth will automatically result
                        in a pit as punishment, and, if necessary, an additional
                        discussion will be held with the GMs and player in
                        question to see if any further consequences are
                        required.
                    </span>
                    <br />
                    <span className="text-uppercase">
                        <strong>unattended macro checks</strong>
                    </span>
                    <span>
                        Theoretically, you would need to be able to respond
                        immediately. However, we do understand that an immediate
                        response might be difficult, and even when performing
                        the actions yourself, you may dream away a little bit.
                        In the end, we can't see what you're doing in your
                        house, in your area, if you're actually running a macro
                        or not.
                    </span>
                    <br />
                    <span>
                        An unattended macro check means the GM will appear
                        around you and expect a, actual response to their
                        appearance (A conversation). They will wait for 90
                        seconds. Anything past that will be seen as unattended
                        macroing, and the above consequences will be executed.
                    </span>
                    <span>
                        Dropping candies, creating fires, dropping notes in
                        front of the person will not be seen as a unattended
                        macro check.
                    </span>
                    <br />
                    <span>
                        As you are required to look at the screen, there will be
                        no audio signal provided for the player.
                    </span>
                    <br />
                    <span>
                        Excuses for unattended macroing will not be accepted. We
                        simply have no real means to verify any excuse you may
                        give us. This is why it is not an immediate pit and you
                        have several other penalties before resulting in a pit.
                    </span>
                    <br />
                    <span>
                        Unattended macro checks will happen periodically, by
                        different GMs, on different times, and to everyone
                        online that we cannot immediately perceive as 'not
                        macroing'.
                    </span>
                    <br />
                    <span>
                        If you want to not worry about these things, and still
                        want to macro, my advice is to turn it off when you are
                        not 100% sure that you can actually respond at any given
                        time. If you need to go to the bathroom, turn your macro
                        off.
                    </span>
                    <br />
                    <span>
                        Unfriendly/hostile and annoyed behaviour during macro
                        checks is heavily frowned upon. Doing these checks is
                        not something we enjoy doing. Based on the severity, the
                        interaction may be reviewed within the GM team and a
                        consequence may be applied.
                    </span>
                    <br />
                    <span>
                        Any changes to these rules will be communicated through
                        the announcements.
                    </span>
                    <br />
                    <span>
                        For any other questions, please feel free to send me
                        (Bobley) a message (on Discord) and I'll help clear out
                        any misunderstandings.
                    </span>
                </div>
            </div>
        </div>
    );
};

export default TermsOfServicePage;
